/**
 * DisplayCurrencyProvider — holds + persists the chosen DISPLAY currency
 * (#43, 2026-06-12).
 *
 * ZAR is the authoritative default. The choice is persisted to localStorage so
 * a returning reader keeps their preferred display. Switching to USD only
 * changes RENDERING — the stored ZAR-cents data is never altered.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  DEFAULT_DISPLAY_CURRENCY,
  formatDisplayAmount,
  type DisplayCurrency,
} from './display';
import { DisplayCurrencyContext, type DisplayCurrencyContextValue } from './context';

const STORAGE_KEY = 'elreign-display-currency';

function readPersisted(): DisplayCurrency {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'ZAR' || raw === 'USD') return raw;
  } catch {
    /* localStorage unavailable — use the authoritative default. */
  }
  return DEFAULT_DISPLAY_CURRENCY;
}

export function DisplayCurrencyProvider({ children }: { children: ReactNode }) {
  const [display, setDisplayState] = useState<DisplayCurrency>(DEFAULT_DISPLAY_CURRENCY);

  // Re-hydrate the persisted choice once on mount.
  useEffect(() => {
    const persisted = readPersisted();
    if (persisted !== DEFAULT_DISPLAY_CURRENCY) setDisplayState(persisted);
  }, []);

  const setDisplay = useCallback((c: DisplayCurrency) => {
    setDisplayState(c);
    try {
      localStorage.setItem(STORAGE_KEY, c);
    } catch {
      /* best-effort persistence. */
    }
  }, []);

  const value = useMemo<DisplayCurrencyContextValue>(
    () => ({
      display,
      setDisplay,
      format: (zarCents: number) => formatDisplayAmount(zarCents, display),
      isIndicative: display === 'USD',
    }),
    [display, setDisplay],
  );

  return <DisplayCurrencyContext value={value}>{children}</DisplayCurrencyContext>;
}
